import { useState } from "react"

import { Datasets } from "../../../../constants/enums"
import { parseErrorAsObj } from "../../../../errors/errorProcessors"
import { IAppError } from "../../../../errors/errorTypes"
import { useV2gCommentRouting } from "../../../../hooks/useV2gCommentRouting"
import { patchClientProtected } from "../../clients/clients"
import {
    LCT_PIN_COMMENT,
    LCT_UNPIN_COMMENT,
    TTR_PIN_COMMENT,
    TTR_UNPIN_COMMENT,
    V2G_PIN_LCT_COMMENT,
    V2G_UNPIN_LCT_COMMENT,
} from "../../urls/private"

interface IPinCommentHook {
    isSending: boolean
    error: IAppError | undefined
    pinComment: (
        deviceSystemRef: string,
        commentId: string,
        dataset?: Datasets
    ) => Promise<void>
    unpinComment: (deviceSystemRef: string, dataset?: Datasets) => Promise<void>
}

/**
 * Provides functions and logic for posting a pin or unpin comment patch request
 * on a TTR/LCT device. Pass `isV2gDevice` when the device is a V2G EVCP so
 * that V2G assessors are routed to the `/v2g/v1/` endpoints.
 */
const usePinComment = (isV2gDevice = false): IPinCommentHook => {
    const [isSending, setIsSending] = useState(false)
    const [error, setError] = useState<IAppError | undefined>(undefined)
    const useAsV2gAssessor = useV2gCommentRouting(isV2gDevice)

    const pinComment = async (
        deviceSystemRef: string,
        commentId: string,
        dataset?: Datasets
    ): Promise<void> => {
        try {
            setIsSending(true)

            const body = {
                deviceSystemRef,
                commentId,
            }
            if (
                dataset &&
                ![Datasets.TTR, Datasets.TTR_DRAFT].includes(dataset)
            ) {
                const url = useAsV2gAssessor
                    ? V2G_PIN_LCT_COMMENT(deviceSystemRef, commentId, dataset)
                    : LCT_PIN_COMMENT(deviceSystemRef, commentId, dataset)
                await patchClientProtected(url, body)
            } else {
                await patchClientProtected(
                    TTR_PIN_COMMENT(deviceSystemRef, commentId),
                    body
                )
            }
            setError(undefined)
        } catch (err) {
            const errorObj = parseErrorAsObj(err)
            setError(errorObj)
        } finally {
            setIsSending(false)
        }
    }

    const unpinComment = async (
        deviceSystemRef: string,
        dataset?: Datasets
    ): Promise<void> => {
        try {
            setIsSending(true)

            const body = {
                deviceSystemRef,
            }

            if (
                dataset &&
                ![Datasets.TTR, Datasets.TTR_DRAFT].includes(dataset)
            ) {
                const url = useAsV2gAssessor
                    ? V2G_UNPIN_LCT_COMMENT(deviceSystemRef, dataset)
                    : LCT_UNPIN_COMMENT(deviceSystemRef, dataset)
                await patchClientProtected(url, body)
            } else {
                await patchClientProtected(
                    TTR_UNPIN_COMMENT(deviceSystemRef),
                    body
                )
            }
            setError(undefined)
        } catch (err) {
            const errorObj = parseErrorAsObj(err)
            setError(errorObj)
        } finally {
            setIsSending(false)
        }
    }

    return {
        error,
        isSending,
        pinComment,
        unpinComment,
    }
}

export default usePinComment
